import { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useGetCustomClinicalCharacteristicsMutation } from '@verantos-dev/data-access-cohort-summary';
import { useConceptGroupsForCustomCharacteristics } from '@verantos-dev/data-access-concept-groups';
import { DomainType, CohortSummaryAttributeResult } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import {
  cohortCharacteristicResultTypeToSectionNameMap,
  cohortCharacteristicResultTypeToCustomSectionNameMap,
} from '../constants';
import { useCharacteristicsConceptGroupsMapping } from './useCharacteristicsConceptGroupsMapping';
import { useCustomConceptSelection, ConceptGroupStats } from './useCustomConceptSelection';
import {
  CustomClinicalCharacteristicsApiService,
  type CustomClinicalCharacteristicsRequest,
} from '../services/customClinicalCharacteristicsApi';
import {
  mapConceptGroupsToStats,
  sortConceptGroupsByCount,
  findMatchingConceptGroups,
} from '../utils/conceptGroupsUtils';
import { mapDomainToApiDomain } from '../utils/domainMappingUtils';

export interface UseCohortCharacteristicsWithCustomSelectionProps {
  projectId: string;
  cohortId: string;
  selectedDomain: DomainType | null;
  cohortSummaryData?: Record<DomainType, CohortSummaryAttributeResult[]>;
}

export const useCohortCharacteristicsWithCustomSelection = ({
  projectId,
  cohortId,
  selectedDomain,
  cohortSummaryData,
}: UseCohortCharacteristicsWithCustomSelectionProps) => {
  const {
    getSelectionState,
    setSelectedConceptGroups,
    setAvailableConceptGroups,
    enableCustomMode,
    disableCustomMode,
    isInCustomMode,
    hasCustomSelection,
  } = useCustomConceptSelection();

  const intl = useIntl();

  // API hooks
  const [getCustomCharacteristics] = useGetCustomClinicalCharacteristicsMutation();

  const {
    allConceptGroups,
    isConceptGroupsLoading,
    mapStatsToConceptGroups,
    mapConceptGroupsToDropdownItems,
  } = useCharacteristicsConceptGroupsMapping(selectedDomain, cohortId);

  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [isLoadingCustomData, setIsLoadingCustomData] = useState(false);
  const [customDataError, setCustomDataError] = useState<string | null>(null);

  const syncComboboxWithChartData = useCallback(
    (domain: DomainType, chartData: CohortSummaryAttributeResult[]) => {
      if (!chartData || chartData.length === 0) return;

      const availableStats = getSelectionState(domain).availableConceptGroups;
      if (!availableStats || availableStats.length === 0) return;

      const matchingStats = findMatchingConceptGroups(chartData, availableStats);
      setSelectedConceptGroups(domain, matchingStats);
    },
    [getSelectionState, setSelectedConceptGroups]
  );

  const {
    conceptGroups: dialogConceptGroups,
    isLoading: isDialogConceptGroupsLoading,
    error: conceptGroupsError,
    fetchConceptGroups,
  } = useConceptGroupsForCustomCharacteristics();

  const [pendingChartData, setPendingChartData] = useState<CohortSummaryAttributeResult[] | null>(
    null
  );

  useEffect(() => {
    if (selectedDomain) {
      // Clear available concept groups when switching domains to prevent showing stale data
      setAvailableConceptGroups(selectedDomain, []);

      if (dialogConceptGroups && dialogConceptGroups.length > 0) {
        loadAvailableConceptGroups(selectedDomain);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain, dialogConceptGroups, setAvailableConceptGroups]);

  const availableConceptGroups = selectedDomain
    ? getSelectionState(selectedDomain).availableConceptGroups
    : [];

  useEffect(() => {
    if (selectedDomain && pendingChartData && availableConceptGroups.length > 0) {
      const timeoutId = setTimeout(() => {
        syncComboboxWithChartData(selectedDomain, pendingChartData);
        setPendingChartData(null);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedDomain, pendingChartData, availableConceptGroups, syncComboboxWithChartData]);

  useEffect(() => {
    if (conceptGroupsError) {
      setCustomDataError('Failed to load concept groups for this domain');
    }
  }, [conceptGroupsError]);

  const handleData = useCallback(
    (chartData?: CohortSummaryAttributeResult[]) => {
      if (!selectedDomain) return;

      const isInCustomModeForDomain = isInCustomMode(selectedDomain);
      const dataToStore =
        chartData || (!isInCustomModeForDomain ? cohortSummaryData?.[selectedDomain] : undefined);

      if (dataToStore) {
        setPendingChartData(dataToStore);
      }

      fetchConceptGroups({
        cohortProfileId: cohortId,
        domain: mapDomainToApiDomain(selectedDomain)?.toLowerCase(),
      });
    },
    [
      selectedDomain,
      cohortId,
      fetchConceptGroups,
      mapDomainToApiDomain,
      cohortSummaryData,
      isInCustomMode,
      getSelectionState,
    ]
  );

  const loadAvailableConceptGroups = useCallback(
    async (domain: DomainType) => {
      try {
        const conceptGroupsStats = mapConceptGroupsToStats(dialogConceptGroups, domain);
        const sortedConceptGroupsStats = sortConceptGroupsByCount(conceptGroupsStats);

        setAvailableConceptGroups(domain, sortedConceptGroupsStats);

        if (!isDialogConceptGroupsLoading && domain === selectedDomain) {
          setTimeout(() => {
            handleData();
          }, 100);
        }
      } catch {
        setCustomDataError('Failed to load available concept groups');
      }
    },
    [
      dialogConceptGroups,
      setAvailableConceptGroups,
      isDialogConceptGroupsLoading,
      selectedDomain,
      handleData,
    ]
  );

  const handleOpenCustomDialog = useCallback(
    (chartData?: CohortSummaryAttributeResult[]) => {
      if (!selectedDomain) return;

      setIsCustomDialogOpen(true);
      setCustomDataError(null);

      handleData(chartData);
    },
    [selectedDomain, handleData]
  );

  const handleCloseCustomDialog = useCallback(() => {
    setIsCustomDialogOpen(false);
    setPendingChartData(null);
  }, []);

  const handleApplyCustomSelection = useCallback(
    async (domain: DomainType, selectedConceptGroups: ConceptGroup[]) => {
      try {
        setIsLoadingCustomData(true);
        setCustomDataError(null);

        // Call the API to get custom clinical characteristics
        const conceptGroupIds = selectedConceptGroups.map((cg) => cg.conceptGroupId);
        const request: CustomClinicalCharacteristicsRequest = {
          conceptGroups: conceptGroupIds,
          domain: domain,
        };

        const response =
          await CustomClinicalCharacteristicsApiService.getCustomClinicalCharacteristics(
            projectId,
            cohortId,
            request,
            getCustomCharacteristics
          );

        if (response && response.data) {
          // Enable custom mode and store the actual API response data
          enableCustomMode(domain);

          // The API response contains characteristics data directly
          const characteristicsData: CohortSummaryAttributeResult[] =
            response.data.characteristics || [];

          // Always set the selection data, even if empty - this ensures empty state is shown
          if (characteristicsData.length === 0) {
            // When characteristics are empty, set empty array to show empty state
            setSelectedConceptGroups(domain, []);
          } else {
            // Map API response data to ConceptGroupStats format for chart display
            const selectedStats: ConceptGroupStats[] = characteristicsData.map((item, index) => ({
              id: `${domain}-${item.attributeName || index}`,
              name: item.attributeName,
              count: item.attributeCount || 0,
              percentage: item.attributePercentage || 0,
            }));

            setSelectedConceptGroups(domain, selectedStats);
          }

          setIsCustomDialogOpen(false);
          setPendingChartData(null);
        } else {
          setCustomDataError('Failed to apply custom selection');
        }
      } catch {
        setCustomDataError('Failed to apply custom selection');
      } finally {
        setIsLoadingCustomData(false);
      }
    },
    [projectId, cohortId, enableCustomMode, setSelectedConceptGroups, getCustomCharacteristics]
  );

  const handleResetToDefault = useCallback(
    (domain: DomainType) => {
      disableCustomMode(domain);
      setCustomDataError(null);
    },
    [disableCustomMode]
  );

  const getChartTitle = useCallback(
    (domain: DomainType): string | undefined => {
      if (isInCustomMode(domain)) {
        const customMap = cohortCharacteristicResultTypeToCustomSectionNameMap[domain];
        return customMap ? intl.formatMessage({ id: customMap }) : undefined;
      }

      const defaultMap = cohortCharacteristicResultTypeToSectionNameMap[domain];
      return defaultMap ? intl.formatMessage({ id: defaultMap }) : undefined;
    },
    [isInCustomMode, intl]
  );

  const getDisplayData = useCallback(
    (domain: DomainType, defaultData: CohortSummaryAttributeResult[]) => {
      if (isInCustomMode(domain)) {
        const selectionState = getSelectionState(domain);
        const customData = selectionState.selectedConceptGroups.map((group) => ({
          attributeName: group.name,
          attributeCount: group.count,
          attributePercentage: group.percentage,
        }));

        return customData;
      }
      return defaultData;
    },
    [isInCustomMode, getSelectionState]
  );

  return {
    isCustomDialogOpen,
    handleOpenCustomDialog: handleOpenCustomDialog,
    handleCloseCustomDialog,

    isLoadingAvailableGroups: isConceptGroupsLoading || isDialogConceptGroupsLoading,
    isLoadingCustomData,
    customDataError,

    isInCustomMode: selectedDomain ? isInCustomMode(selectedDomain) : false,
    hasCustomSelection: selectedDomain ? hasCustomSelection(selectedDomain) : false,
    getSelectionState: selectedDomain ? () => getSelectionState(selectedDomain) : undefined,

    handleApplyCustomSelection: selectedDomain
      ? (selectedGroups: ConceptGroup[]) =>
          handleApplyCustomSelection(selectedDomain, selectedGroups)
      : undefined,
    handleResetToDefault: selectedDomain ? () => handleResetToDefault(selectedDomain) : undefined,

    getChartTitle: selectedDomain ? () => getChartTitle(selectedDomain) : () => 'Top 10',
    chartTitle: selectedDomain ? getChartTitle(selectedDomain) : undefined,
    getDisplayData: selectedDomain
      ? (defaultData: CohortSummaryAttributeResult[]) => getDisplayData(selectedDomain, defaultData)
      : (defaultData: CohortSummaryAttributeResult[]) => defaultData,

    allConceptGroups,
    isConceptGroupsLoading,
    getAvailableConceptGroups: selectedDomain
      ? () => {
          const stats = getSelectionState(selectedDomain).availableConceptGroups;
          return mapStatsToConceptGroups(stats);
        }
      : () => [],
    getSelectedConceptGroups: selectedDomain
      ? () => {
          const stats = getSelectionState(selectedDomain).selectedConceptGroups;
          return mapStatsToConceptGroups(stats);
        }
      : () => [],
    getAvailableConceptGroupsAsDropdownItems: selectedDomain
      ? () => {
          const stats = getSelectionState(selectedDomain).availableConceptGroups;
          const conceptGroups = mapStatsToConceptGroups(stats);
          return mapConceptGroupsToDropdownItems(conceptGroups);
        }
      : () => [],
    getSelectedConceptGroupsAsDropdownItems: selectedDomain
      ? () => {
          const stats = getSelectionState(selectedDomain).selectedConceptGroups;

          // Filter duplicates by name before mapping
          const uniqueStats = stats.filter(
            (stat, index, array) =>
              array.findIndex(
                (s) => s.name.toLowerCase().trim() === stat.name.toLowerCase().trim()
              ) === index
          );

          const conceptGroups = mapStatsToConceptGroups(uniqueStats);
          return mapConceptGroupsToDropdownItems(conceptGroups);
        }
      : () => [],

    // Bidirectional conversion helpers
    syncComboboxWithChartData: selectedDomain
      ? (chartData: CohortSummaryAttributeResult[]) =>
          syncComboboxWithChartData(selectedDomain, chartData)
      : undefined,
    openCustomDialogWithChartData: selectedDomain
      ? (chartData: CohortSummaryAttributeResult[]) => {
          handleOpenCustomDialog(chartData);
          // Set chart data for mapping after concept groups load
          setTimeout(() => {
            if (getSelectionState(selectedDomain).availableConceptGroups.length > 0) {
              syncComboboxWithChartData(selectedDomain, chartData);
            }
          }, 100);
        }
      : undefined,
  };
};
